package features.timeoff.data

import core.firebase.FirebaseService
import features.timeoff.domain.LeaveCategory
import features.timeoff.domain.LeaveType
import kotlin.js.Promise
import kotlin.js.json

/**
 * Repository for managing leave categories and types in Firestore
 * Categories are stored per team to ensure isolation
 */
object LeaveCategoryRepository {
    
    /**
     * Get all leave categories for a team
     */
    fun getLeaveCategories(teamId: String): Promise<List<LeaveCategory>> {
        console.log("📋 Fetching leave categories for team: $teamId")
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("leave_categories")
            .get()
            .then { snapshot: dynamic ->
                val categories = mutableListOf<LeaveCategory>()
                
                snapshot.forEach { doc: dynamic ->
                    val rawData: dynamic = doc.data()
                    val category = LeaveCategory(
                        id = doc.id,
                        typeId = rawData.typeId as? String ?: "",
                        name = rawData.name as? String ?: "",
                        quota = (rawData.quota as? Double)?.toInt() ?: 0,
                        deductible = rawData.deductible as? Boolean ?: true,
                        documentationRequired = rawData.documentationRequired as? Boolean ?: false,
                        cutSalary = rawData.cutSalary as? Boolean ?: false,
                        color = rawData.color as? String ?: "slate"
                    )
                    categories.add(category)
                }
                
                console.log("✅ Loaded ${categories.size} leave categories")
                categories
            }
            .catch { error: dynamic ->
                console.error("❌ Failed to fetch leave categories:", error)
                throw error
            }
    }
    
    /**
     * Add a new leave category to a team
     */
    fun addLeaveCategory(
        teamId: String,
        name: String,
        typeId: String,
        quota: Int = 0
    ): Promise<String> {
        console.log("📝 Adding leave category: $name for team: $teamId")
        
        val categoryData = json(
            "name" to name,
            "typeId" to typeId,
            "quota" to quota,
            "deductible" to true,
            "documentationRequired" to false,
            "cutSalary" to false,
            "color" to "slate",
            "createdAt" to js("firebase.firestore.FieldValue.serverTimestamp()")
        )
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("leave_categories")
            .add(categoryData)
            .then { docRef: dynamic ->
                val categoryId = docRef.id
                console.log("✅ Leave category created with ID: $categoryId")
                categoryId
            }
            .catch { error: dynamic ->
                console.error("❌ Failed to add leave category:", error)
                throw error
            }
    }
    
    /**
     * Delete a leave category
     */
    fun deleteLeaveCategory(teamId: String, categoryId: String): Promise<Unit> {
        console.log("🗑️ Deleting leave category: $categoryId")
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("leave_categories")
            .doc(categoryId)
            .delete()
            .then {
                console.log("✅ Leave category deleted")
                Unit
            }
            .catch { error: dynamic ->
                console.error("❌ Failed to delete leave category:", error)
                throw error
            }
    }
    
    /**
     * Get all leave types for a team
     */
    fun getLeaveTypes(teamId: String): Promise<List<LeaveType>> {
        console.log("📋 Fetching leave types for team: $teamId")
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("leave_types")
            .get()
            .then { snapshot: dynamic ->
                val types = mutableListOf<LeaveType>()
                
                snapshot.forEach { doc: dynamic ->
                    val rawData: dynamic = doc.data()
                    val type = LeaveType(
                        id = doc.id,
                        name = rawData.name as? String ?: "",
                        quota = (rawData.quota as? Double)?.toInt() ?: 0,
                        default = rawData.default as? Boolean ?: false
                    )
                    types.add(type)
                }
                
                console.log("✅ Loaded ${types.size} leave types")
                types
            }
            .catch { error: dynamic ->
                console.error("❌ Failed to fetch leave types:", error)
                throw error
            }
    }
    
    /**
     * Create a new leave type for a team
     */
    fun createLeaveType(
        teamId: String,
        name: String,
        quota: Int,
        isDefault: Boolean = false
    ): Promise<String> {
        console.log("📝 Creating leave type: $name for team: $teamId")
        
        val typeData = json(
            "name" to name,
            "quota" to quota,
            "default" to isDefault,
            "createdAt" to js("firebase.firestore.FieldValue.serverTimestamp()")
        )
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("leave_types")
            .add(typeData)
            .then { docRef: dynamic ->
                val typeId = docRef.id
                console.log("✅ Leave type created with ID: $typeId")
                typeId
            }
            .catch { error: dynamic ->
                console.error("❌ Failed to create leave type:", error)
                throw error
            }
    }
    
    /**
     * Delete a leave type
     */
    fun deleteLeaveType(teamId: String, typeId: String): Promise<Unit> {
        console.log("🗑️ Deleting leave type: $typeId")
        
        return FirebaseService.db
            .collection("teams")
            .doc(teamId)
            .collection("leave_types")
            .doc(typeId)
            .delete()
            .then {
                console.log("✅ Leave type deleted")
                Unit
            }
            .catch { error: dynamic ->
                console.error("❌ Failed to delete leave type:", error)
                throw error
            }
    }
}
